import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

export default function SearchableSelectDropdown({
  label,
  options,
  value = '',
  values = [],
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matching options.',
  helperText = '',
  disabled = false,
  multiple = false,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedValues = useMemo(
    () => (multiple ? (Array.isArray(values) ? values : []) : []),
    [multiple, values]
  );
  const selectedValue = multiple ? '' : (typeof value === 'string' ? value : '');

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.toLowerCase().includes(term));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickAway = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [open]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setQuery('');
    }
  }, [disabled]);

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
    if (open) setQuery('');
  };

  const handleToggleMultiple = (option) => {
    const nextValues = selectedValues.includes(option)
      ? selectedValues.filter((item) => item !== option)
      : [...selectedValues, option];
    onChange(nextValues);
  };

  const handleSelectSingle = (option) => {
    onChange(option);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="form-group searchable-select" ref={rootRef}>
      <span className="form-label">{label}</span>
      {helperText && <p className="searchable-select__helper text-muted">{helperText}</p>}

      <div
        className={`searchable-select__control ${open ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
        onClick={toggleOpen}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleOpen();
          }
          if (event.key === 'Escape') {
            setOpen(false);
            setQuery('');
          }
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {multiple ? (
          <div className="searchable-select__chips">
            {selectedValues.length > 0 ? selectedValues.map((selected) => (
              <button
                key={selected}
                type="button"
                className="searchable-select__chip"
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggleMultiple(selected);
                }}
                disabled={disabled}
              >
                <span>{selected}</span>
                <span aria-hidden="true">×</span>
              </button>
            )) : <span className="searchable-select__placeholder">{placeholder}</span>}
          </div>
        ) : (
          <span className={selectedValue ? 'searchable-select__value' : 'searchable-select__placeholder'}>
            {selectedValue || placeholder}
          </span>
        )}

        <span className="searchable-select__chevron" aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
      </div>

      {open && (
        <div className="searchable-select__menu">
          <input
            type="text"
            className="form-input searchable-select__search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            autoFocus
          />

          <div className="searchable-select__options" role="listbox" aria-multiselectable={multiple || undefined}>
            {filteredOptions.length > 0 ? filteredOptions.map((option) => {
              const selected = multiple ? selectedValues.includes(option) : selectedValue === option;
              return (
                <label key={option} className={`searchable-select__option ${selected ? 'is-selected' : ''}`}>
                  <input
                    type={multiple ? 'checkbox' : 'radio'}
                    checked={selected}
                    onChange={() => {
                      if (multiple) handleToggleMultiple(option);
                      else handleSelectSingle(option);
                    }}
                    name={multiple ? undefined : label}
                  />
                  <span>{option}</span>
                </label>
              );
            }) : (
              <div className="searchable-select__empty text-muted">{emptyMessage}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

SearchableSelectDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  emptyMessage: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
};
